import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Activity, MousePointerClick, Eye, Users, RefreshCw, Radio,
  TrendingUp, ArrowUpRight,
} from 'lucide-react';

/**
 * AdminAnalytics — real-time first-party analytics dashboard.
 *
 * Data source: GET /api/admin/analytics/summary (admin-only). The page
 * auto-refreshes every 30s so admins get near-live visibility of who's
 * on the site and what they're clicking, without leaving the admin
 * portal. Complements Google Analytics 4 (which has a 24h aggregation
 * delay) with instant totals and the CTA-per-page heatmap that GA4
 * can't render out-of-the-box.
 */
export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get('/admin/analytics/summary');
      setData(d);
      setLastFetch(new Date());
    } catch (_e) { /* toast already handled globally */ }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => { setRefreshing(true); load(); }, 30_000);
    return () => clearInterval(id);
  }, []);

  const t24 = data?.totals?.last_24h || {};
  const t7 = data?.totals?.last_7d || {};
  const t30 = data?.totals?.last_30d || {};

  const heatmapMaxClicks = useMemo(
    () => (data?.cta_heatmap_7d || []).reduce((m, r) => Math.max(m, r.clicks || 0), 1),
    [data]
  );

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto' data-testid='admin-analytics-page'>
      {/* Header */}
      <div className='flex items-start sm:items-center justify-between mb-6 flex-wrap gap-3'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Live Traffic</div>
          <h1 className='font-display text-2xl sm:text-3xl font-bold mt-1 text-slate-900 dark:text-slate-50'>
            Analytics
          </h1>
          <p className='text-slate-500 dark:text-slate-400 text-sm mt-1 flex items-center gap-2'>
            <Radio className='w-3.5 h-3.5 text-emerald-500 animate-pulse' />
            Real-time first-party tracker
            {lastFetch && (
              <span className='text-xs text-slate-400 dark:text-slate-500'>
                · updated {lastFetch.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={() => { setRefreshing(true); load(); }}
          variant='outline'
          className='border-slate-200 dark:border-slate-700'
          disabled={refreshing}
          data-testid='analytics-refresh-btn'
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Live now — outsized card so it's the first thing the eye lands on. */}
      <Card
        className='p-5 sm:p-6 mb-5 bg-gradient-to-br from-[#1B3A87] to-[#0B1220] text-white border-none shadow-xl'
        data-testid='analytics-live-card'
      >
        <div className='flex items-center justify-between flex-wrap gap-3'>
          <div>
            <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#93C5FD] flex items-center gap-2'>
              <span className='w-2 h-2 rounded-full bg-emerald-400 animate-pulse' />
              Active right now &middot; last 5 min
            </div>
            <div className='font-display text-5xl sm:text-6xl font-bold tabular-nums mt-2'>
              {loading ? '—' : (data?.live_sessions_5m ?? 0)}
            </div>
            <div className='text-sm text-slate-200 mt-1'>
              unique visitor{(data?.live_sessions_5m ?? 0) === 1 ? '' : 's'} on your site
            </div>
          </div>
          <div className='flex items-center gap-2 text-slate-200 text-xs'>
            <Radio className='w-4 h-4 text-emerald-400' />
            Auto-refreshes every 30s
          </div>
        </div>
      </Card>

      {/* KPI cards — 24h · 7d · 30d rollups. */}
      <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
        {[
          { l: 'Visitors · 24h', v: t24.unique_sessions, icon: Users, color: '#1B3A87', tid: 'kpi-visitors-24h' },
          { l: 'Page views · 24h', v: t24.page_views, icon: Eye, color: '#167A5A', tid: 'kpi-pageviews-24h' },
          { l: 'CTA clicks · 24h', v: t24.cta_clicks, icon: MousePointerClick, color: '#B45309', tid: 'kpi-clicks-24h' },
          { l: 'Leads · 24h', v: t24.leads, icon: TrendingUp, color: '#C8102E', tid: 'kpi-leads-24h' },
        ].map((k) => (
          <Card
            key={k.l}
            className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'
            data-testid={k.tid}
          >
            <div className='flex items-center justify-between'>
              <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400'>{k.l}</div>
              <k.icon className='w-4 h-4' style={{ color: k.color }} />
            </div>
            <div className='font-display text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-50 mt-2'>
              {loading ? '—' : (k.v ?? 0)}
            </div>
          </Card>
        ))}
      </div>

      {/* 7d / 30d comparison strip */}
      <Card className='p-4 mb-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
          {[
            { label: '7-day visitors',  value: t7.unique_sessions,  hint: `${t7.page_views ?? 0} page views` },
            { label: '7-day CTA clicks', value: t7.cta_clicks,       hint: `${t7.leads ?? 0} leads` },
            { label: '30-day visitors',  value: t30.unique_sessions, hint: `${t30.page_views ?? 0} page views` },
            { label: '30-day CTA clicks',value: t30.cta_clicks,      hint: `${t30.leads ?? 0} leads` },
          ].map((r) => (
            <div key={r.label} data-testid={`analytics-longterm-${r.label.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className='text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400'>{r.label}</div>
              <div className='font-display text-xl sm:text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-50 mt-1'>
                {loading ? '—' : (r.value ?? 0)}
              </div>
              <div className='text-xs text-slate-500 dark:text-slate-400'>{r.hint}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabs — Top pages · Top CTAs · Heatmap · Live feed */}
      <Tabs defaultValue='pages' className='w-full'>
        <TabsList className='mb-4 flex-wrap'>
          <TabsTrigger value='pages' data-testid='analytics-tab-pages'>Top Pages</TabsTrigger>
          <TabsTrigger value='ctas' data-testid='analytics-tab-ctas'>Top CTAs</TabsTrigger>
          <TabsTrigger value='heatmap' data-testid='analytics-tab-heatmap'>Click Heatmap</TabsTrigger>
          <TabsTrigger value='feed' data-testid='analytics-tab-feed'>Live Feed</TabsTrigger>
        </TabsList>

        {/* ------ Top pages ------ */}
        <TabsContent value='pages'>
          <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='font-display font-semibold text-lg text-slate-900 dark:text-slate-50'>
                Top pages &middot; last 7 days
              </h3>
              <Badge variant='outline' className='text-[10px]'>{(data?.top_pages_7d?.length) || 0}</Badge>
            </div>
            {loading ? (
              <div className='py-6 text-sm text-slate-500'>Loading…</div>
            ) : (data?.top_pages_7d?.length || 0) === 0 ? (
              <EmptyState hint='No page views logged yet. Visit a few public pages to seed data.' />
            ) : (
              <div className='space-y-2'>
                {data.top_pages_7d.map((row) => {
                  const max = data.top_pages_7d[0]?.views || 1;
                  const pct = Math.max(4, Math.round((row.views / max) * 100));
                  return (
                    <div
                      key={row.path}
                      className='relative overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3'
                      data-testid={`analytics-top-page-${(row.path || 'na').replace(/[^\w-]/g, '_')}`}
                    >
                      <div
                        className='absolute inset-y-0 left-0 bg-[#1B3A87]/10 dark:bg-[#93C5FD]/10'
                        style={{ width: `${pct}%` }}
                      />
                      <div className='relative flex items-center justify-between gap-3'>
                        <a
                          href={row.path}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='font-mono text-sm text-slate-900 dark:text-slate-50 truncate hover:text-[#1B3A87] dark:hover:text-[#93C5FD] inline-flex items-center gap-1'
                        >
                          {row.path || '/'} <ArrowUpRight className='w-3 h-3 shrink-0' />
                        </a>
                        <div className='flex items-center gap-3 text-xs tabular-nums shrink-0'>
                          <span className='text-slate-500 dark:text-slate-400'>
                            {row.unique_sessions} visitor{row.unique_sessions === 1 ? '' : 's'}
                          </span>
                          <span className='font-bold text-slate-900 dark:text-slate-50'>
                            {row.views} view{row.views === 1 ? '' : 's'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ------ Top CTAs ------ */}
        <TabsContent value='ctas'>
          <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='font-display font-semibold text-lg text-slate-900 dark:text-slate-50'>
                Top CTA clicks &middot; last 7 days
              </h3>
              <Badge variant='outline' className='text-[10px]'>{(data?.top_ctas_7d?.length) || 0}</Badge>
            </div>
            {loading ? (
              <div className='py-6 text-sm text-slate-500'>Loading…</div>
            ) : (data?.top_ctas_7d?.length || 0) === 0 ? (
              <EmptyState hint='No CTA clicks yet. Wait a bit or click through the site to seed data.' />
            ) : (
              <div className='space-y-2'>
                {data.top_ctas_7d.map((row) => {
                  const max = data.top_ctas_7d[0]?.clicks || 1;
                  const pct = Math.max(4, Math.round((row.clicks / max) * 100));
                  return (
                    <div
                      key={row.target}
                      className='relative overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3'
                      data-testid={`analytics-top-cta-${row.target.replace(/[^\w-]/g, '_')}`}
                    >
                      <div
                        className='absolute inset-y-0 left-0 bg-[#C8102E]/10'
                        style={{ width: `${pct}%` }}
                      />
                      <div className='relative flex items-center justify-between gap-3'>
                        <div className='font-semibold text-sm text-slate-900 dark:text-slate-50'>
                          {prettifyCta(row.target)}
                        </div>
                        <div className='text-xs font-bold text-slate-900 dark:text-slate-50 tabular-nums'>
                          {row.clicks} click{row.clicks === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ------ Heatmap — CTA × page ------ */}
        <TabsContent value='heatmap'>
          <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='font-display font-semibold text-lg text-slate-900 dark:text-slate-50'>
                Click heatmap &middot; last 7 days
              </h3>
              <p className='text-xs text-slate-500 dark:text-slate-400'>Which CTA wins on which page</p>
            </div>
            {loading ? (
              <div className='py-6 text-sm text-slate-500'>Loading…</div>
            ) : (data?.cta_heatmap_7d?.length || 0) === 0 ? (
              <EmptyState hint='No CTA clicks logged yet.' />
            ) : (
              <div className='overflow-x-auto -mx-1 px-1'>
                <table className='w-full text-sm min-w-[520px]' data-testid='analytics-heatmap-table'>
                  <thead>
                    <tr className='border-b border-slate-200 dark:border-slate-700'>
                      <th className='text-left py-2 pr-3 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400'>Page</th>
                      <th className='text-left py-2 pr-3 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400'>CTA</th>
                      <th className='text-right py-2 pl-3 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400'>Clicks</th>
                      <th className='text-left py-2 pl-4 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400 w-1/3'>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cta_heatmap_7d.map((row, i) => {
                      const pct = Math.max(4, Math.round((row.clicks / heatmapMaxClicks) * 100));
                      const isTop = i === 0;
                      return (
                        <tr
                          key={`${row.path}::${row.target}`}
                          className='border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60'
                        >
                          <td className='py-2 pr-3 font-mono text-xs truncate max-w-[180px]'>{row.path || '/'}</td>
                          <td className='py-2 pr-3 font-medium'>{prettifyCta(row.target)}{isTop && <Badge className='ml-2 bg-[#C8102E]/15 text-[#C8102E] hover:bg-[#C8102E]/15 text-[9px]'>Top</Badge>}</td>
                          <td className='py-2 pl-3 text-right tabular-nums font-bold'>{row.clicks}</td>
                          <td className='py-2 pl-4'>
                            <div className='h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden'>
                              <div className='h-full bg-[#C8102E]' style={{ width: `${pct}%` }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ------ Live feed ------ */}
        <TabsContent value='feed'>
          <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='font-display font-semibold text-lg text-slate-900 dark:text-slate-50'>
                Live activity feed &middot; last 20 events
              </h3>
              <Activity className='w-4 h-4 text-emerald-500' />
            </div>
            {loading ? (
              <div className='py-6 text-sm text-slate-500'>Loading…</div>
            ) : (data?.recent_events?.length || 0) === 0 ? (
              <EmptyState hint='No events captured yet.' />
            ) : (
              <div className='divide-y divide-slate-100 dark:divide-slate-800'>
                {data.recent_events.map((e) => (
                  <div
                    key={e.id}
                    className='py-2.5 flex items-start gap-3 text-sm'
                    data-testid={`analytics-feed-${e.id}`}
                  >
                    <EventDot type={e.event_type} />
                    <div className='flex-1 min-w-0'>
                      <div className='font-semibold text-slate-900 dark:text-slate-50 truncate'>
                        {prettifyEvent(e)}
                      </div>
                      <div className='text-xs text-slate-500 dark:text-slate-400 truncate'>
                        {e.path || '/'} {e.location ? `· ${e.location}` : ''}
                      </div>
                    </div>
                    <div className='text-[10px] text-slate-400 tabular-nums shrink-0'>
                      {formatRelative(e.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- helpers ----------

const EmptyState = ({ hint }) => (
  <div className='py-8 text-center'>
    <div className='text-sm text-slate-500 dark:text-slate-400'>{hint}</div>
  </div>
);

const EventDot = ({ type }) => {
  const map = {
    page_view: 'bg-[#1B3A87]',
    cta_click: 'bg-[#C8102E]',
    generate_lead: 'bg-emerald-500',
    message_greg_open: 'bg-[#7de3d9]',
    region_map_tap: 'bg-[#B45309]',
  };
  return <span className={`w-2 h-2 rounded-full mt-2 shrink-0 ${map[type] || 'bg-slate-400'}`} />;
};

const prettifyEvent = (e) => {
  if (e.event_type === 'page_view') return 'Page view';
  if (e.event_type === 'cta_click') return `CTA click · ${prettifyCta(e.target)}`;
  if (e.event_type === 'generate_lead') return `Lead submitted · ${e.target || 'demo_form'}`;
  if (e.event_type === 'message_greg_open') return 'Message Greg opened';
  if (e.event_type === 'region_map_tap') return `Region tap · ${e.target || 'unknown'}`;
  return e.event_type;
};

const prettifyCta = (t) => {
  if (!t) return '(unlabelled)';
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  const diff = Date.now() - ts;
  if (diff < 60_000) return `${Math.max(1, Math.round(diff / 1000))}s ago`;
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return `${Math.round(diff / 86_400_000)}d ago`;
};
